import React from 'react';
import { Helmet } from 'react-helmet';
import { Box, Typography, Button, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const { REACT_APP_SERVER } = process.env;
const accentColor = REACT_APP_SERVER === 'ATS' ? '#0973b9' : '#7ebc0a';
const accentHover = REACT_APP_SERVER === 'ATS' ? '#065a94' : '#6aa309';

function NotFound() {
    return (
        <>
            <Helmet>
                <title>Not found</title>
            </Helmet>
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                }}
            >
                {/* Left panel – accent */}
                <Box
                    sx={{
                        flex: { xs: '0 0 auto', md: '1 1 40%' },
                        minHeight: { xs: 220, md: '100vh' },
                        bgcolor: accentColor,
                        color: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -40,
                            right: -40,
                            width: 200,
                            height: 200,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.08)',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -60,
                            left: -60,
                            width: 220,
                            height: 220,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.06)',
                        }}
                    />
                    <PhoneDisabledIcon sx={{ fontSize: 64, mb: 2, opacity: 0.95 }} />
                    <Typography variant="h2" component="h1" fontWeight={800} sx={{ letterSpacing: 4, opacity: 0.98 }}>
                        404
                    </Typography>
                    <Typography variant="overline" sx={{ mt: 1, letterSpacing: 2, opacity: 0.9 }}>
                        Line not in service
                    </Typography>
                </Box>

                {/* Right panel – content */}
                <Box
                    sx={{
                        flex: { xs: '1 1 auto', md: '1 1 60%' },
                        minHeight: { xs: '50vh', md: '100vh' },
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4,
                        textAlign: { xs: 'center', md: 'left' },
                    }}
                >
                    <Box sx={{ maxWidth: 400 }}>
                        <Typography variant="h5" fontWeight={700} color="text.primary" sx={{ mb: 1.5 }}>
                            Page not found
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            The page or extension you&apos;re looking for could not be found. Check the address or return to the main panel.
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 3 }}>
                            Status: Line not in service · Code: 404
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <Button
                                component={Link}
                                to="/"
                                variant="contained"
                                startIcon={<HomeIcon />}
                                sx={{
                                    bgcolor: accentColor,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1.25,
                                    '&:hover': { bgcolor: accentHover },
                                }}
                            >
                                Main panel
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => window.history.back()}
                                sx={{
                                    borderColor: 'divider',
                                    color: 'text.secondary',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': { borderColor: accentColor, color: accentColor, bgcolor: `${accentColor}08` },
                                }}
                            >
                                Go back
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </>
    );
}

export default NotFound;
